// Página de boas-vindas do Nexo — nova tela inicial do ambiente dos funcionários.
//
// Reaproveita o MESMO componente de mascote (NexoMascot) e o painel de ajuda existente (useHelpPanel). Não
// mostra o mascote flutuante aqui (o NexoHelp esconde o lançador nesta rota); "Preciso de ajuda" abre o
// painel existente — sem áudio, sem microfone, sem registrar humor/produtividade. Nenhuma interação com o
// mascote é obrigatória para acessar o sistema: Dashboard e atalhos estão sempre disponíveis.
//
// Distribuição: bloco de saudação centralizado (mascote + saudação + respostas + ações) e, abaixo, a grade
// "Suas rotinas" em LARGURA TOTAL (até 6 colunas em telas largas).
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Coffee, LayoutDashboard, LifeBuoy, Rocket, Smile } from "lucide-react";
import { useApp } from "@/store/useApp";
import { useHelpPanel } from "@/store/useHelpPanel";
import { useAdminTabs } from "@/store/useAdminTabs";
import { useNexoWelcome, type WelcomeResponse } from "@/store/useNexoWelcome";
import { NexoMascot, type NexoAnimation } from "@/components/nexo/NexoMascot";
import {
  brasiliaHour,
  getStaffHomePreference,
  nextGreetingRotation,
  periodForHour,
  pickGreeting,
  primaryDashboardFor,
  setStaffHomePreference,
  staffFirstName,
  welcomeShortcuts,
  type StaffHomePreference,
} from "@/lib/staffWelcome";
import { cn } from "@/lib/utils";

const REACTION: Record<WelcomeResponse, string> = {
  bem: "Que bom saber! Estou por aqui se você precisar de algo.",
  corrido: "Sem problema. Posso te levar direto aos seus atalhos.",
  trabalhar: "Vamos lá! Seus atalhos estão logo abaixo.",
  ajuda: "Claro! Abri o painel de ajuda para você.",
};

const RESPONSES: { key: WelcomeResponse; label: string; icon: typeof Smile }[] = [
  { key: "bem", label: "Estou bem 😊", icon: Smile },
  { key: "corrido", label: "Dia corrido!", icon: Coffee },
  { key: "trabalhar", label: "Vamos trabalhar!", icon: Rocket },
  { key: "ajuda", label: "Preciso de ajuda", icon: LifeBuoy },
];

function animationFor(phase: string, response: WelcomeResponse | null): NexoAnimation {
  if (phase === "waiting") return "wave";
  if (phase === "breathing") return "idle";
  switch (response) {
    case "bem":
      return "happy";
    case "trabalhar":
      return "jumping";
    default:
      return "talking"; // "corrido" e "ajuda" → falando (sem áudio)
  }
}

export default function AdminWelcome() {
  const navigate = useNavigate();
  const account = useApp((s) => s.account);
  const supplierNfEnabled = useApp((s) => s.supplierNfEnabled);
  const openMenu = useHelpPanel((s) => s.openMenu);
  const reduceMotion = useHelpPanel((s) => s.reduceMotion);
  // Uma aba interna ativa significa que a home de boas-vindas NÃO é a superfície em foco → pausa a animação.
  const internalTabActive = useAdminTabs((s) => s.activeKey !== null);

  const greeting = useNexoWelcome((s) => s.greeting);
  const phase = useNexoWelcome((s) => s.phase);
  const response = useNexoWelcome((s) => s.response);
  const reactionMessage = useNexoWelcome((s) => s.reactionMessage);

  // Pausa a animação quando a janela/aba do NAVEGADOR está oculta OU quando outra aba INTERNA está ativa
  // (não anima fora da vista). Combina as duas condições pedidas.
  const [windowHidden, setWindowHidden] = useState(() => typeof document !== "undefined" && document.hidden);
  useEffect(() => {
    const onVis = () => setWindowHidden(document.hidden);
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);
  const paused = windowHidden || internalTabActive;

  // Inicializa a saudação UMA vez por usuário/abertura, chaveando pelo ID ESTÁVEL da conta. Voltar de uma aba
  // (remount da home) NÃO reinicia: ensureGreeting é idempotente por dono. Só avança a rotação de frases
  // quando cria uma saudação nova.
  useEffect(() => {
    const ownerId = account?.id ?? account?.email;
    if (!ownerId) return;
    const state = useNexoWelcome.getState();
    if (state.ownerId === ownerId && state.greeting) return;
    const period = periodForHour(brasiliaHour());
    const text = pickGreeting(period, staffFirstName(account), nextGreetingRotation());
    state.ensureGreeting(ownerId, period, text);
    // Chaveia só pelo ID: voltar à home (remount) com a MESMA conta não deve reiniciar a saudação.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account?.id]);

  // Após a reação breve, assenta na respiração (repouso). O timer sobrevive a remount porque a fase vive no
  // store: se a home desmontar durante a reação, ao voltar o efeito reagenda e assenta.
  useEffect(() => {
    if (phase !== "reacting") return;
    const t = window.setTimeout(() => useNexoWelcome.getState().settle(), 2600);
    return () => window.clearTimeout(t);
  }, [phase, response]);

  const primaryDashboard = useMemo(() => primaryDashboardFor(account), [account]);
  const shortcuts = useMemo(
    () => welcomeShortcuts(account, supplierNfEnabled),
    [account, supplierNfEnabled],
  );

  const [homePref, setHomePref] = useState<StaffHomePreference>(() =>
    getStaffHomePreference(account?.id),
  );
  useEffect(() => {
    setHomePref(getStaffHomePreference(account?.id));
  }, [account?.id]);

  if (!account) return null;

  const animation = animationFor(phase, response);

  const respond = (key: WelcomeResponse) => {
    useNexoWelcome.getState().react(key, REACTION[key]);
    if (key === "ajuda") openMenu();
  };

  const toggleHomePref = (checked: boolean) => {
    const pref: StaffHomePreference = checked ? "dashboard" : "welcome";
    setStaffHomePreference(account.id, pref);
    setHomePref(pref);
  };

  return (
    <div className="min-h-full w-full bg-background px-4 py-8 sm:py-10">
      <div className="mx-auto w-full max-w-6xl">
        {/* Bloco de saudação — centralizado. */}
        <div className="mx-auto flex max-w-2xl flex-col items-center gap-5 text-center">
          <NexoMascot
            animation={animation}
            reduceMotion={reduceMotion}
            paused={paused}
            size={132}
            title="Nexo, mascote da Naturexpress"
          />

          <div aria-live="polite" className="space-y-2">
            <h1 className="text-balance text-2xl font-semibold leading-tight text-foreground sm:text-3xl">
              {greeting ?? "Olá! Que bom ter você por aqui."}
            </h1>
            {phase === "reacting" && reactionMessage ? (
              <p className="text-sm text-muted-foreground sm:text-base">{reactionMessage}</p>
            ) : (
              <p className="text-sm text-muted-foreground sm:text-base">
                Como você está hoje? Responda ao Nexo ou vá direto às suas rotinas.
              </p>
            )}
          </div>

          {/* Botões de resposta ao Nexo — NÃO bloqueiam o acesso: são opcionais e ficam sempre disponíveis. */}
          <div className="flex flex-wrap items-center justify-center gap-2" role="group" aria-label="Responder ao Nexo">
            {RESPONSES.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                type="button"
                onClick={() => respond(key)}
                aria-pressed={response === key}
                className={cn(
                  "inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-colors",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
                  response === key
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-background text-foreground hover:bg-secondary/60",
                )}
              >
                <Icon className="h-4 w-4" aria-hidden="true" />
                {label}
              </button>
            ))}
          </div>

          {/* Acesso ao Dashboard (conforme permissão). "Preciso de ajuda" NÃO se repete aqui — é o chip de
              resposta acima, que abre o painel de ajuda existente. */}
          {primaryDashboard && (
            <div className="flex flex-wrap items-center justify-center gap-2">
              <button
                type="button"
                onClick={() => navigate(primaryDashboard)}
                className={cn(
                  "inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
                )}
              >
                <LayoutDashboard className="h-4 w-4" aria-hidden="true" />
                Abrir Dashboard
              </button>
            </div>
          )}
        </div>

        {/* Atalhos das rotinas permitidas (derivados da navegação atual) — LARGURA TOTAL, até 6 colunas. */}
        {shortcuts.length > 0 && (
          <section className="mt-10" aria-label="Atalhos das suas rotinas">
            <h2 className="mb-3 text-sm font-semibold text-primary">Suas rotinas</h2>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
              {shortcuts.map(({ to, label, icon: Icon }) => (
                <button
                  key={to}
                  type="button"
                  onClick={() => navigate(to)}
                  className={cn(
                    "flex items-center gap-2 rounded-lg border border-border bg-background px-3 py-2.5 text-left text-sm font-medium text-foreground transition-colors hover:bg-secondary/60",
                    "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
                  )}
                >
                  <Icon className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                  <span className="truncate">{label}</span>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* Preferência por usuário (só neste navegador). Só aparece para quem tem um Dashboard permitido —
            sem criar opção sem efeito para quem não tem acesso a nenhum painel. */}
        {primaryDashboard && (
          <label className="mt-6 inline-flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
            <input
              type="checkbox"
              checked={homePref === "dashboard"}
              onChange={(e) => toggleHomePref(e.target.checked)}
              className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
            />
            Usar meu Dashboard como página inicial
          </label>
        )}
      </div>
    </div>
  );
}
